/**
 * Project Profitability — dashboard KPI tile (Feb 2026).
 *
 * Surfaces per-project revenue vs cost so admins/supervisors can see at a
 * glance which projects are profitable, which are losing money, and where
 * to focus the next round of cost cuts. Mirrors EstimatePerformanceTile's
 * shape so the dashboard stays visually consistent.
 *
 * Data: GET /api/analytics/projects/profitability?days=30|90|365|0
 *
 * Permissions: admins + supervisors only (the endpoint 403s for workers,
 * and we additionally hide the tile in the dashboard wrapper).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, TrendingDown, AlertTriangle, Loader2, FolderKanban, ArrowRight,
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
  { key: 365, label: '1y' },
  { key: 0, label: 'All' },
];

// Compact money: $1.2k, $34k, $1.1M — keeps the tile readable when
// companies start grossing real volume per project.
const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const marginColor = (pct) => {
  if (pct >= 30) return '#10B981';   // healthy
  if (pct >= 10) return '#F59E0B';   // thin
  return '#EF4444';                  // loss / very thin
};

export default function ProjectProfitabilityTile() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/analytics/projects/profitability?days=${days}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, token]);

  if (loading) {
    return (
      <div className="ht-card p-5" data-testid="project-profit-tile">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading project profitability…
        </div>
      </div>
    );
  }

  const rows = data?.rows || [];
  const losers = data?.losers || [];
  const totals = data?.totals || {};
  const totalMargin = totals.margin_pct || 0;
  const byCustomer = data?.mode === 'customer';

  return (
    <div className="ht-card p-5" data-testid="project-profit-tile">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(99,102,241,0.15)' }}
          >
            <FolderKanban className="w-5 h-5" style={{ color: '#818CF8' }} />
          </div>
          <div>
            <h3 className="text-white font-semibold">{byCustomer ? 'Job profitability' : 'Project profitability'}</h3>
            <p className="text-[10px] text-slate-500" data-testid="profit-mode">{byCustomer ? 'By customer — no project-tagged jobs in this period' : 'By project'}</p>
          </div>
        </div>
        <div className="flex gap-1" data-testid="profit-period-toggle">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setDays(p.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                days === p.key ? 'text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
              style={{
                background: days === p.key ? '#6366F1' : 'transparent',
                border: days === p.key ? 'none' : '1px solid #2D3748',
              }}
              data-testid={`profit-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Headline KPIs ── revenue, profit (with sign-aware colour), margin */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Revenue</div>
          <div className="text-xl font-bold text-white mt-1" data-testid="profit-revenue">
            {fmtMoney(totals.revenue)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{totals.jobs || 0} jobs</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Profit</div>
          <div
            className="text-xl font-bold mt-1 flex items-center gap-1"
            style={{ color: (totals.profit || 0) >= 0 ? '#10B981' : '#EF4444' }}
            data-testid="profit-amount"
          >
            {(totals.profit || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {fmtMoney(totals.profit)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Cost {fmtMoney(totals.cost)}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Margin</div>
          <div
            className="text-xl font-bold mt-1"
            style={{ color: marginColor(totalMargin) }}
            data-testid="profit-margin"
          >
            {totalMargin.toFixed(1)}%
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{totals.hours || 0}h worked</div>
        </div>
      </div>

      {/* Loss-makers callout ─ projects sliding into the red. Shows up only
          when there are any so the tile stays tidy on healthy companies. */}
      {losers.length > 0 && (
        <div
          className="rounded-lg p-3 mb-3 flex items-start gap-2"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
          data-testid="profit-losers-callout"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
          <div className="text-xs text-slate-200">
            <span className="font-semibold text-red-300">
              {losers.length} {byCustomer ? 'customer' : 'project'}{losers.length === 1 ? '' : 's'} at risk:
            </span>{' '}
            {losers.slice(0, 3).map((l, i) => (
              <span key={l.project_id}>
                {i > 0 && ' · '}
                <span className="font-mono text-red-200">{l.project_number || l.project_name}</span>
                {' '}
                <span style={{ color: marginColor(l.margin_pct) }}>{l.margin_pct}%</span>
              </span>
            ))}
            {losers.length > 3 && ` · +${losers.length - 3} more`}
          </div>
        </div>
      )}

      {/* Ranked rows ─ top 5 by revenue. Click-through routes to Settings
          → Projects so the admin can drill into the project record. */}
      {rows.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-500" data-testid="profit-empty">
          No jobs with hours or billing in this period yet{days ? ' — try a longer range' : ''}.
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 px-2">
            <div className="col-span-5">{byCustomer ? 'Customer' : 'Project'}</div>
            <div className="col-span-2 text-right">Revenue</div>
            <div className="col-span-2 text-right">Profit</div>
            <div className="col-span-2 text-right">Margin</div>
            <div className="col-span-1 text-right">Jobs</div>
          </div>
          {rows.slice(0, 5).map((r) => (
            <button
              key={r.project_id}
              onClick={() => navigate(byCustomer ? '/jobs' : '/settings?section=projects')}
              data-testid={`profit-row-${r.project_id}`}
              className="w-full grid grid-cols-12 gap-2 px-2 py-2 rounded-md text-left transition-colors hover:bg-white/5"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            >
              <div className="col-span-5 min-w-0">
                {r.project_number && <div className="font-mono text-xs text-emerald-400 truncate">{r.project_number}</div>}
                <div className="text-xs text-white truncate">{r.project_name}</div>
                {!byCustomer && r.customer_name && (
                  <div className="text-[10px] text-slate-500 truncate">{r.customer_name}</div>
                )}
                {byCustomer && <div className="text-[10px] text-slate-500 truncate">{r.completed_count} completed · {r.total_hours}h</div>}
              </div>
              <div className="col-span-2 text-right text-sm text-white">{fmtMoney(r.revenue)}</div>
              <div
                className="col-span-2 text-right text-sm font-medium"
                style={{ color: r.profit >= 0 ? '#10B981' : '#EF4444' }}
              >
                {fmtMoney(r.profit)}
              </div>
              <div
                className="col-span-2 text-right text-sm font-medium"
                style={{ color: marginColor(r.margin_pct) }}
              >
                {r.margin_pct.toFixed(0)}%
              </div>
              <div className="col-span-1 text-right text-xs text-slate-400">{r.jobs_count}</div>
            </button>
          ))}
          {rows.length > 5 && (
            <button
              onClick={() => navigate('/settings?section=projects')}
              className="w-full text-xs text-slate-400 hover:text-slate-200 py-2 flex items-center justify-center gap-1"
              data-testid="profit-view-all-btn"
            >
              View all {rows.length} projects <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
